import { useAppTFunction } from '@retail/app/i18n';
import { useController, useFormContext } from 'react-hook-form';
import { CreateFreightConditionSchema, FreightConditionForm } from '@retail/freight/components';
import { useCallback } from 'react';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useConditionActions, useItemSearch } from '@retail/calculus/context';
import { useFetchGlobalItemUnits } from '@retail/freight/data-access';
import { getConditionBindingValuesFromLevelSchema } from '@retail/calculus/components';
import { Unit } from '@retail/products/types';
import { CreateConditionDialog } from '../ConditionActionsBase';

interface Props {
    onClose: () => void;
    open: boolean;
    supplierNumber: number;
}

function CreateFreightConditionDialog({ onClose, open, supplierNumber }: Props) {
    const t = useAppTFunction();

    const { createOrUpdateCondition, isLoadingPostChanges } = useConditionActions();

    const { data: globalItemUnitsDTO = [] } = useFetchGlobalItemUnits();
    const globalItemUnits = globalItemUnitsDTO.map(Unit.fromDto);

    const { handleSubmit, control } =
        useFormContext<CreateFreightConditionSchema>();
    const itemController = useController({ name: 'item', control });

    const { mgItem } = useItemSearch({ supplierId: supplierNumber, selectedMgItemNumber: itemController.field.value?.mgItemNumber });
    const supplierItemPackages =
        mgItem?.getSupplierItem(supplierNumber)?.mgSupplierItemPackages ?? [];

    const onSubmit = useCallback(
        (levelSchema: CreateFreightConditionSchema) => {
            createOrUpdateCondition({
                content: {
                    freight: levelSchema.percentAddon
                        ? { percentAddon: levelSchema.percentAddon }
                        : undefined,
                    freightFixed:
                        levelSchema.fixedAddon && levelSchema.fixedAddonUnit
                            ? { fixedAddOn: levelSchema.fixedAddon, fixedAddOnUnit: levelSchema.fixedAddonUnit }
                            : undefined,
                },
                bindingValue: getConditionBindingValuesFromLevelSchema({
                    levelSchema,
                    supplierNumber,
                }),
                validFrom: levelSchema.fromDate,
            }).then(onClose);
        },
        [createOrUpdateCondition, supplierNumber, onClose]
    );

    return (
        <CreateConditionDialog
            open={open}
            title={t('freight.newFreightCondition')}
            supplierNumber={supplierNumber}
            isLoading={isLoadingPostChanges}
            isDisabled={isLoadingPostChanges}
            dialogContent={
                <FreightConditionForm
                    supplierItemPackages={supplierItemPackages}
                    globalItemUnits={globalItemUnits}
                />
            }
            onSubmit={handleSubmit((data) => onSubmit(data))}
            onClose={onClose}
        />
    );
}

export default withDialogSuspenseErrorBoundary(CreateFreightConditionDialog);
